/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import * as fs from "fs";
import * as path from "path";

import Long = require("long");

import { calculateTenantId, sanitizeTenantUuid } from "../utils/TenantId";
import { checkStringArrayOption } from "../utils/Validators";
import { LoggingDestination, LoggingFlags } from "../Types";

// ============================================================================
// used to store the Configuration to avoid parsing it more then once
let dtConfiguration: Readonly<DtConfiguration> | undefined;

// ============================================================================
interface ConnectionOptions {
	baseUrl?: string;
	authToken?: string;
	proxy?: string;
	certificates?: string[];
}

interface TestabilityOptions {
	spanProcessingIntervalMs?: number;
	logLevel?: string;
}

interface LoggingOptions {
	destination?: "stdout" | "stderr" | "none";
	nodejs?: {
		flags?: string;
	};
}

interface RumOptions {
	applicationId?: string;
	clientIpHeaders?: string[];
}

interface DebugOptions {
	addStackOnStart?: boolean;
}

export interface DtOptions {
	clusterID?: number;
	tenant?: string;
	connection?: ConnectionOptions;
	testability?: TestabilityOptions;
	logging?: LoggingOptions;
	rum?: RumOptions;
	debug?: DebugOptions;
}

//=============================================================================
export interface DtConfiguration {
	clusterId?: number;
	tenantUUID?: string;
	tenantId?: number;
	agentID?: Long;
	baseUrl?: string;
	authToken?: string;
	spanProcessingIntervalMs?: number;
	loggingDestination?: LoggingDestination;
	loggingFlags?: LoggingFlags;
	rumClientIpHeaders?: string[];
	rumApplicationId?: string;
	debugAddStackOnStart?: boolean;
}

// ----------------------------------------------------------------------------
/**
 * Represents the content of a read configuration file.
 * Intentionally set types to unknown and properties to optional to ensure proper
 * checking needs to be done.
 */
interface FileConfig {
	ClusterID?: unknown;
	Tenant?: unknown;
	Connection?: {
		AuthToken?: unknown;
		BaseUrl?: unknown;
		Proxy?: unknown;
		Certificates?: unknown;
	};
	Testability?: {
		SpanProcessingIntervalMs?: unknown;
	};
	Logging?: {
		Destination?: unknown;
		NodeJs?: {
			Flags?: unknown;
		};
	};
	RUM?: {
		ApplicationId?: unknown;
		ClientIpHeaders?: unknown;
	};
	Debug?: {
		AddStackOnStart?: boolean;
	};
}

// ----------------------------------------------------------------------------
/**
 * Builds up configuration using options passed during initialization, Environment
 * variables and configuration files.
 * Following priority is used:
 * - Options given as arguments
 * - Options from environment
 * - Options from config file
 * @param options options provided programatically
 * @returns configuration
 *
 * Note: this function is only exported for tests, use `getConfiguration()` instead.
 */
export function detectConfiguration(options?: Readonly<DtOptions>): Readonly<DtConfiguration> {
	const fileConfig = getConfigFromFile();

	const clusterId = numberOrUndefined(options?.clusterID
		?? getIntFromEnv("DT_CLUSTER_ID")
		?? fileConfig.ClusterID);

	const tenantUUID = stringOrUndefined(options?.tenant
		?? process.env.DT_TENANT
		?? fileConfig.Tenant);

	// Connection options
	const baseUrl = stringOrUndefined(options?.connection?.baseUrl
		?? process.env.DT_CONNECTION_BASE_URL
		?? fileConfig.Connection?.BaseUrl);

	const authToken = stringOrUndefined(options?.connection?.authToken
		?? process.env.DT_CONNECTION_AUTH_TOKEN
		?? fileConfig.Connection?.AuthToken);

	// Testability
	const spanProcessingIntervalMs = numberOrUndefined(options?.testability?.spanProcessingIntervalMs
		?? getIntFromEnv("DT_TESTABILITY_SPAN_PROCESSING_INTERVAL_MS")
		?? fileConfig.Testability?.SpanProcessingIntervalMs);

	// Logging
	const val = options?.logging?.destination
		?? process.env.DT_LOGGING_DESTINATION
		?? fileConfig.Logging?.Destination;
	let loggingDestination: LoggingDestination | undefined;
	switch (val) {
		case LoggingDestination.Off:
		case LoggingDestination.StdErr:
		case LoggingDestination.StdOut:
			loggingDestination = val;
			break;
	}

	let loggingFlags: LoggingFlags | undefined;
	const loggingFlagsString = stringOrUndefined(options?.logging?.nodejs?.flags
		?? process.env.DT_LOGGING_NODEJS_FLAGS
		?? fileConfig.Logging?.NodeJs?.Flags);
	if (loggingFlagsString != null) {
		loggingFlags = Object.create(null);
		const flags = parseList(loggingFlagsString, ",");
		for (const flag of flags) {
			const [name, value] = parseFlag(flag);
			if (name != null) {
				loggingFlags![name] = value;
			}
		}
	}

	// RUM
	let rumClientIpHeaders: string[] | undefined;
	if (options?.rum?.clientIpHeaders != null) {
		if (checkStringArrayOption(options?.rum?.clientIpHeaders)) {
			rumClientIpHeaders = options?.rum?.clientIpHeaders;
		}
	}
	if (rumClientIpHeaders == null && typeof(process.env.DT_RUM_CLIENT_IP_HEADERS) === "string") {
		rumClientIpHeaders = parseList(process.env.DT_RUM_CLIENT_IP_HEADERS, ":");
	}
	if (rumClientIpHeaders == null && fileConfig.RUM?.ClientIpHeaders != null) {
		if (checkStringArrayOption(fileConfig.RUM?.ClientIpHeaders)) {
			rumClientIpHeaders = fileConfig.RUM?.ClientIpHeaders;
		}
	}
	rumClientIpHeaders = rumClientIpHeaders?.map(s => s.toLowerCase().trim()) ?? undefined;

	const rumApplicationId = stringOrUndefined(options?.rum?.applicationId
		?? process.env.DT_RUM_APPLICATION_ID
		?? fileConfig.RUM?.ApplicationId);

	// Debug
	const debugAddStackOnStart = getBoolean(options?.debug?.addStackOnStart
		?? process.env.DT_DEBUG_ADD_STACK_ON_START
		?? fileConfig.Debug?.AddStackOnStart);

	const sanitizedTenantUUID = tenantUUID != null ? sanitizeTenantUuid(tenantUUID) : undefined;
	return {
		clusterId,
		tenantUUID: sanitizedTenantUUID,
		tenantId: sanitizedTenantUUID != null ? calculateTenantId(sanitizedTenantUUID) : undefined,
		agentID: generateAgentId(),
		baseUrl,
		authToken,
		spanProcessingIntervalMs,
		loggingDestination,
		loggingFlags,
		rumClientIpHeaders,
		rumApplicationId,
		debugAddStackOnStart
	};
}

// ----------------------------------------------------------------------------
/**
 * Builds up configuration using options passed, Environment variables and configuration files.
 * Following priority is used:
 * - Options given as function arguments
 * - Options from environment
 * - Options from config files
 * The detected configuration is cached and returned on followup calls.
 * @param options optional options provided programatically (e.g. Commandline)
 * @returns configuration retrieved
 */
export function getConfiguration(options?: Readonly<DtOptions>): Readonly<DtConfiguration> {
	if (dtConfiguration == null) {
		dtConfiguration = detectConfiguration(options);
	}
	return dtConfiguration;
}

// ----------------------------------------------------------------------------
/**
 * Reads configuration from file. If configuration file is cached then the cached config will be returned.
 * Following order is used:
 * - File referenced by environment variable DT_CONFIG_FILE (if set)
 * - dtconfig.json in current working directory (if existing)
 * if parsing of a file fails no fallback is used.
 * @returns The parsed JSON read or an empty object
 */
function getConfigFromFile(): FileConfig {
	let configFile: FileConfig;
	try {
		const json = fs.readFileSync(process.env.DT_CONFIG_FILE ?? path.join(process.cwd(), "dtconfig.json"), { encoding: "utf8" });
		configFile = JSON.parse(json);
	} catch  {
		// ignore parse error/missing file
		configFile = Object.create(null);
	}
	return configFile;
}

// ----------------------------------------------------------------------------
/**
 * Parses a number from an environment variable.
 * @param name name of environment variable
 * @returns the parsed number (may be NaN) or undefined if variable doesn't exist
 */
function getIntFromEnv(name: string): number | undefined {
	const val = process.env[name];
	if (val != null) {
		return parseInt(val, 10);
	}
	return undefined;
}

// ----------------------------------------------------------------------------
function numberOrUndefined(val: unknown): number | undefined {
	return (typeof(val) === "number" && !isNaN(val)) ? val : undefined;
}

// ----------------------------------------------------------------------------
function stringOrUndefined(val: unknown): string | undefined {
	return (typeof(val) === "string" && val.length > 0) ? val : undefined;
}

/**
 * Gracefully parses a variable to a boolean value.
 * Variables with boolean values should gracefully evaluate to true or false.
 * All values that do not case insensitively match to "false" or "0" evaluate to true.
 * @param value value to parse
 * @returns false if the value case insensitively matches to "false", "0", or it is null; true otherwise
 */
function getBoolean(val: unknown): boolean {
	if (val == null) {
		return false;
	}
	if (typeof (val) === "string") {
		return toBoolean(val);
	}
	if (typeof (val) === "boolean") {
		return val;
	}
	if (typeof (val) === "number") {
		if (isNaN(val)) {
			return false;
		}
		return val !== 0;
	}
	return true;
}
// ----------------------------------------------------------------------------
/**
 * Parse a logging flag to name/value.
 * @param flag flag to parse
 */
function parseFlag(flag: string): [string, boolean] | [] {
	const tokens = flag.split("=", 3);
	if (tokens.length !== 2 || tokens[0].length === 0 || tokens[1].length === 0) {
		return [];
	}
	return [tokens[0].trim(), toBoolean(tokens[1])];
}

// ----------------------------------------------------------------------------
/**
 * Graceful convert a string to a boolean.
 * @param value the string to convert
 */
function toBoolean(value: string): boolean {
	const lcVal = value.trim().toLowerCase();
	return (lcVal !== "false") && (lcVal !== "0");
}

// ----------------------------------------------------------------------------
/**
 * Parse string formated as list and trim whitespace from each entry.
 * @param value string to parse
 * @param separator
 */
function parseList(value: string, separator: string): string[] {
	if (value.length === 0) {
		return [];
	}
	return value.split(separator).map(s => s.trim());
}

// ----------------------------------------------------------------------------
/**
 * Generates a random agent Id
 * @returns The 64 bit agent id
 */
function generateAgentId(): Long {
	const to32Bit = 2 ** 32 - 1;
	return new Long(Math.random() * to32Bit, Math.random() * to32Bit);
}

// ----------------------------------------------------------------------------
/// only exported for unittests
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function _testSetConfig(c?: any): void {
	dtConfiguration = c;
}
